import * as React from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { calculateTotalPages } from './paginated-table-utils';
import { PaginatedTableModel, PaginatedTableRowModel } from './PaginatedTable.types';

const StyledPaginatedTable = styled.div`
  .paginated-table-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 8px 0;
  }

  .paginated-table-controls {
    display: inline-flex;
    > * {
      min-width: 48px;
      font-size: 0.9rem;
    }
    button {
      cursor: pointer;
      background: none;
      padding: 0.5rem 1rem;
      border: 1px solid rgba(0, 0, 0, 0.1);
      margin-left: -1px;
      &:first-child {
        margin: 0;
      }
      &:hover {
        background: rgba(0, 0, 0, 0.1);
      }
    }
    > span {
      border: 1px solid rgba(0, 0, 0, 0.1);
      padding: 0.5rem 1rem;
      margin-left: -1px;
    }

    .pagination-control {
      border-radius: 8px 0 0 8px;
      svg {
        width: 1rem;
        height: 1rem;
        display: block;
      }
      &.right {
        border-radius: 0 8px 8px 0;
        svg {
          transform: rotate(180deg);
        }
      }
    }
    @media (max-width: 767px) {
      .page {
        display: none;
      }
    }
  }

  .paginated-table-recap {
    > span {
      font-weight: 700;
    }
  }

  .paginated-table {
    width: 100%;
    overflow: hidden;
    border-spacing: 0;
    border-radius: 8px;

    thead {
      tr {
        border: none;
      }

      th {
        padding: 1rem 0.5rem;
        font-weight: 700;
        white-space: nowrap;
        font-size: 0.9rem;
        text-align: center;

        @media (min-width: 767px) {
          font-size: 1rem;
          padding: 1rem;
        }

        &:first-child {
          text-align: left;
        }

        &:last-child {
          text-align: right;
        }
      }
    }

    tr {
      td {
        vertical-align: middle;
        border: none;
        font-size: 0.8rem;
        padding: 0.5rem;

        @media (min-width: 767px) {
          font-size: 1rem;
          padding: 1rem;
        }

        &.center {
          text-align: center;
        }

        &.right {
          text-align: right;
        }

        &.no-data,
        &.loading {
          padding: 2rem;
          text-align: center;
        }

        .subtext {
          font-size: 14px;
          opacity: 0.8;
          margin-top: 4px;
        }
      }
    }

    tfoot {
      tr {
        td {
          background: white;
        }
      }
    }
  }
`;

export function PaginatedTable<T>({
  headerCells,
  dataRows,
  isLoading,
  noDataMsg,
  loadingMsg,
  rowsMapper,
  pageSize = 20,
}: PaginatedTableModel<T>): JSX.Element {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [visibleItems, setVisibleItems] = useState<PaginatedTableRowModel[]>([]);
  const [paginationControls, setPaginationControls] = useState<(number | null)[]>([]);

  useEffect(() => {
    // if total pages is 7 or less display all of the pages directly
    if (totalPages <= 7) {
      const pages = Array.from(Array(totalPages).keys()).map(key => key + 1);
      setPaginationControls(pages);
      return;
    }

    // if currentPage is at the first two indices or the last two indices show 3 dots in the middle
    if (currentPage <= 2 || currentPage > totalPages - 2) {
      const pages = [1, 2, 3, null, totalPages - 2, totalPages - 1, totalPages];
      setPaginationControls(pages);
      return;
    }

    const pages = [1, null, currentPage - 1, currentPage, currentPage + 1, null, totalPages];
    setPaginationControls(pages);
  }, [currentPage, totalPages]);

  useEffect(() => {
    const totalItems = dataRows.length;
    setTotalPages(calculateTotalPages(pageSize, totalItems));

    // reset the pager to first page
    const numberOfItems = Math.min(totalItems, pageSize);
    setVisibleItems(rowsMapper(dataRows.slice(0, numberOfItems)));
    setCurrentPage(1);
  }, [dataRows]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setVisibleItems(rowsMapper(dataRows.slice(startIndex, endIndex)));
  }, [currentPage]);

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <StyledPaginatedTable>
      <table className="paginated-table">
        <thead>
          <tr>
            {headerCells.map(cell => (
              <th key={cell}>{cell}</th>
            ))}
          </tr>
        </thead>
        {dataRows.length > 0 && (
          <tbody>
            {visibleItems.map(dataRow => (
              <tr key={dataRow.id}>
                {dataRow.cells.map((cell, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <td className={cell.align} key={`${dataRow.id}-${i}`}>
                    <div>{cell.text}</div>
                    {cell.subtext && <div className="subtext">{cell.subtext}</div>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
        {dataRows.length === 0 && !isLoading && (
          <tbody>
            <tr>
              <td colSpan={4} className="no-data">
                {noDataMsg}
              </td>
            </tr>
          </tbody>
        )}
        {dataRows.length === 0 && isLoading && (
          <tbody>
            <tr>
              <td colSpan={4} className="loading">
                {loadingMsg}
              </td>
            </tr>
          </tbody>
        )}
        {totalPages > 1 && (
          <tfoot>
            <tr>
              <td colSpan={headerCells.length}>
                <div className="paginated-table-footer">
                  <div className="paginated-table-controls">
                    <button
                      type="button"
                      className="pagination-control left"
                      onClick={() => prevPage()}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z" />
                      </svg>
                    </button>
                    {paginationControls.map((page, i) => {
                      if (page !== null) {
                        return (
                          <button
                            /* eslint-disable-next-line react/no-array-index-key */
                            key={`pagination-control-${i}`}
                            type="button"
                            className={`${page === currentPage ? 'active' : null} page`}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        );
                      }
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <span className="page" key={`pagination-control-fill-${i}`}>
                          ...
                        </span>
                      );
                    })}
                    <button
                      type="button"
                      className="pagination-control right"
                      onClick={() => nextPage()}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </StyledPaginatedTable>
  );
}
